import React from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { lovelaceToAda } from "@/lib/governance";
import { formatBigCount } from "@/lib/format";

const bAxis = (v) => {
  const n = Number(v);
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(0)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return `${n}`;
};

// Treasury balance trend. Data: governance/treasury.epochs[] (asc).
// `detailed` adds X/Y axes + gridlines for the full-width overview card.
export const TreasurySparkline = ({ epochs = [], height = 90, detailed = false }) => {
  const data = epochs
    .filter((e) => e && e.treasury != null)
    .map((e) => ({ epoch: e.epoch, ada: lovelaceToAda(e.treasury) }));
  if (data.length < 2) return null;
  return (
    <div data-testid="treasury-sparkline" style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={detailed ? { top: 8, right: 10, bottom: 18, left: 4 } : { top: 4, right: 4, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="treasuryFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
            </linearGradient>
          </defs>
          {detailed ? (
            <CartesianGrid vertical={false} stroke="hsl(var(--primary) / 0.1)" strokeDasharray="3 3" />
          ) : null}
          {detailed ? (
            <XAxis
              dataKey="epoch"
              minTickGap={18}
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "monospace" }}
              tickLine={false}
              axisLine={{ stroke: "hsl(var(--primary) / 0.2)" }}
              label={{ value: "Epoch", position: "insideBottom", offset: -8, fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "monospace" }}
            />
          ) : null}
          <YAxis
            hide={!detailed}
            domain={["dataMin", "dataMax"]}
            width={detailed ? 46 : 0}
            tick={detailed ? { fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "monospace" } : false}
            tickLine={false}
            axisLine={false}
            tickFormatter={detailed ? bAxis : undefined}
          />
          <Tooltip
            cursor={{ stroke: "hsl(var(--primary))", strokeWidth: 1, strokeOpacity: 0.3 }}
            contentStyle={{
              background: "hsl(var(--card))",
              border: "1px solid hsl(var(--primary) / 0.25)",
              borderRadius: 8,
              fontSize: 11,
              fontFamily: "monospace",
            }}
            labelStyle={{ color: "hsl(var(--muted-foreground))" }}
            formatter={(v) => [`${formatBigCount(v)} ₳`, "Treasury"]}
            labelFormatter={(_, p) => (p && p[0] ? `Epoch ${p[0].payload.epoch}` : "")}
          />
          <Area
            type="monotone"
            dataKey="ada"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            fill="url(#treasuryFill)"
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
